import { createContext, useContext } from "react";

/**
 * The seam the copied header's notification bell reads, in the same shape as ./session.tsx: the
 * template's `layouts/notification-dropdown.tsx` rendered a module-level demo array, and the data it
 * stands in for belongs to the host app. `apps/admin` supplies Identity's back-office inbox
 * (`GET /api/identity/me/notifications`) at its root; with no provider the bell is simply empty.
 */

export type NotificationSeverity = "info" | "warning" | "critical";

export type NotificationItem = {
  id: string;
  /** The notice's stable, dotted event name — `order.awaiting_approval` — which picks its icon. */
  kind: string;
  title: string;
  body: string;
  severity: NotificationSeverity;
  /** A back-office path, e.g. `/orders/…`, or null. */
  link: string | null;
  occurredAt: string;
  read: boolean;
};

export type NotificationsValue = {
  /** The newest notices, read and unread. */
  items: NotificationItem[];
  /** Every unread notice the caller's permissions reach — the API's own count, never items.filter(). */
  unreadCount: number;
  /** A failed read, in words. Shown in the dropdown instead of pretending the inbox is empty. */
  error: string | null;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  /** Where "View all" goes. */
  viewAllHref: string;
};

const NotificationsContext = createContext<NotificationsValue>({
  items: [],
  unreadCount: 0,
  error: null,
  markRead: async () => {},
  markAllRead: async () => {},
  viewAllHref: "#",
});

export function NotificationsProvider({ value, children }: React.PropsWithChildren<{ value: NotificationsValue }>) {
  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export const useNotifications = () => useContext(NotificationsContext);
